import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';
import styled from 'styled-components';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ItemFavoriteButton from '../../components/items/ItemFavoriteButton';
import { formatListingPriceLabel } from '../../utils/formatPrice';
import { resolveListingBrand } from '../../components/items/ListingDetailShared';
import { FAVORITE_KIND } from '../../utils/favoriteKeys';
import { getFavoriteListingPath, loadFavoritedListings } from '../../utils/favoritesLoader';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem 4rem;
  min-height: 100vh;
  background: #f6f1e8;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const BrowseButton = styled(Link)`
  background-color: var(--primary-color);
  color: #fff;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  text-decoration: none;
  font-weight: 600;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--primary-dark);
  }
`;

const FavoritesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const FavoriteCard = styled(Link)`
  background: transparent;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.92;
  }
`;

const FavoriteImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: var(--text-light);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transform: scale(1.1);
  }
`;

const FavoriteContent = styled.div`
  padding: 0.55rem 0.15rem 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const FavoritePrice = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const FavoriteMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
  min-width: 0;
`;

const MetaBrand = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background-color: var(--bg-primary);
  border-radius: 1rem;
  box-shadow: var(--shadow-sm);

  h3 {
    margin: 1rem auto 1.75rem;
    max-width: 28rem;
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.45;
    color: var(--text-primary);
  }
`;

const FavoritesPage = () => {
  const favoriteIds = useSelector((state) => state.favorites.favoriteItems);
  const [favoriteItems, setFavoriteItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (favoriteIds.length === 0) {
      setFavoriteItems([]);
      return undefined;
    }
    setLoading(true);
    loadFavoritedListings(favoriteIds)
      .then((entries) => {
        if (!cancelled) setFavoriteItems(entries);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [favoriteIds]);

  if (loading && favoriteItems.length === 0 && favoriteIds.length > 0) {
    return <LoadingSpinner text="Loading favorites..." />;
  }

  return (
    <Container>
      <Header>
        <Title>Your Favorites</Title>
        <BrowseButton to="/pre-owned-items">Browse Items</BrowseButton>
      </Header>

      {favoriteItems.length === 0 ? (
        <EmptyState>
          <div style={{ fontSize: '3rem' }}>🤍</div>
          <h3>Save products you like to compare them later.</h3>
          <BrowseButton to="/designers">Start Discovering</BrowseButton>
        </EmptyState>
      ) : (
        <FavoritesGrid>
          {favoriteItems.map(({ item, favoriteKind }) => {
            const imageUrl = item.primary_image
              ? item.primary_image.startsWith('http')
                ? item.primary_image
                : `${typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'http://127.0.0.1:8080' : ''}${item.primary_image}`
              : item.image_url ||
                (item.images && item.images.length > 0
                  ? item.images[0].image.startsWith('http')
                    ? item.images[0].image
                    : `${typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'http://127.0.0.1:8080' : ''}${item.images[0].image}`
                  : null);

            return (
              <FavoriteCard
                key={`${favoriteKind}-${item.id}`}
                to={getFavoriteListingPath(item, favoriteKind)}
                state={{ itemId: item.id }}
              >
                <FavoriteImage style={{ position: 'relative' }}>
                  <ItemFavoriteButton
                    itemId={item.id}
                    favoriteKind={favoriteKind}
                    category={item.category}
                  />
                  {imageUrl ? (
                    <img src={imageUrl} alt={item.title} />
                  ) : (
                    <span>📦</span>
                  )}
                </FavoriteImage>
                <FavoriteContent>
                  <FavoritePrice>{formatListingPriceLabel(item)}</FavoritePrice>
                  <FavoriteMeta>
                    <MetaBrand>{resolveListingBrand(item) || '—'}</MetaBrand>
                    {favoriteKind === FAVORITE_KIND.ITEM && item.condition ? <span>· {item.condition}</span> : null}
                  </FavoriteMeta>
                </FavoriteContent>
              </FavoriteCard>
            );
          })}
        </FavoritesGrid>
      )}
    </Container>
  );
};

export default FavoritesPage;

